<template>
    <transition name="slide-down">
        <div v-if="isVisible" class="onboarding-banner ppv2-my-3 ppv2-pr-2 md:ppv2-p-0">
            <div class="banner-content">
                <!-- Left side: Icon + Message -->
                <div class="banner-left" @mouseenter="showTooltip = true" @mouseleave="showTooltip = false">
                    <div class="icon-wrapper ppv2-text-white">{{ currentStepIndex }}/{{ totalSteps }}</div>
                    <div class="message">
                        <span class="next-step">{{
                            currentStepIndex === totalSteps
                                ? $t('common.onboarding_banner.last_step')
                                : currentStepIndex === 1
                                  ? $t('common.onboarding_banner.first_step')
                                  : $t('common.onboarding_banner.next_step')
                        }}</span>
                        <span class="separator">•</span>
                        <span class="step-title">{{ currentStep.title }}</span>
                    </div>

                    <!-- Tooltip on hover -->
                    <transition name="fade">
                        <div v-if="showTooltip && incompleteItems && incompleteItems.length" class="tooltip-popup">
                            <div class="tooltip-header">
                                {{ $t('common.onboarding_banner.complete_info') }}
                            </div>
                            <ul class="tooltip-list">
                                <li v-for="(item, index) in incompleteItems" :key="index" class="tooltip-item ppv2-text-black">
                                    <a
                                        v-if="item.url"
                                        :href="item.url"
                                        class="ppv2-cursor-pointer ppv2-text-black ppv2-underline"
                                    >
                                        {{ item.label }}
                                    </a>
                                    <span v-else class="ppv2-text-black">{{ item.label }}</span>
                                </li>
                            </ul>
                        </div>
                    </transition>
                </div>

                <!-- Right side: Action button -->
                <div class="banner-right">
                    <a :href="actionUrl" class="action-button poppinsMedium">
                        {{ actionLabel || $t('common.onboarding_banner.configure_now') }}
                    </a>
                </div>

                <!-- Close button (optional) -->
                <button v-if="closeable" @click="handleClose" class="close-button" :aria-label="$t('common.close')">
                    <X :size="16" color="white" />
                </button>
            </div>
        </div>
    </transition>
</template>

<script setup>
import { ref, computed } from 'vue'
import { X } from 'lucide-vue'

const props = defineProps({
    currentStep: {
        type: Object,
        required: true,
        // Expected format: { title: String, items: [{ label: String, url: String, completed: Boolean }] }
    },
    currentStepIndex: {
        type: Number,
        default: 1,
    },
    totalSteps: {
        type: Number,
        default: 1,
    },
    actionUrl: {
        type: String,
        default: '/back-office/settings',
    },
    actionLabel: {
        type: String,
        default: '',
    },
    isVisible: {
        type: Boolean,
        default: true,
    },
    closeable: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['close'])

const showTooltip = ref(false)

// Filter to show only incomplete items
const incompleteItems = computed(() => {
    if (!props.currentStep || !props.currentStep.items) {
        return []
    }
    return props.currentStep.items.filter((item) => !item.completed)
})

const handleClose = () => {
    emit('close')
}
</script>

<style scoped>
.onboarding-banner {
    background: #d3d3d312;
    border: 1px solid #ffffff1a;
    padding: 6px;
    border-radius: 8px;
    position: relative;
    margin-left: 1%;
}

.banner-content {
    max-width: 1400px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    position: relative;
}

.banner-left {
    display: flex;
    align-items: center;
    gap: 12px;
    position: relative;
    cursor: pointer;
}

.icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background-color: #5932ea;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    font-size: 11px;
}

.message {
    display: flex;
    align-items: center;
    gap: 8px;
    color: white;
    font-family: 'Poppins', sans-serif;
}

.next-step {
    font-weight: 500;
    font-size: 13px;
}

.separator {
    opacity: 0.7;
}

.step-title {
    font-weight: 400;
    font-size: 13px;
}

.banner-right {
    display: flex;
    align-items: center;
}

.action-button {
    background: linear-gradient(90deg, #8241d7 0%, #3a329d 100%);
    color: white;
    padding: 8px 20px;
    border-radius: 8px;
    font-size: 13px;
    text-decoration: none;
    transition: all 0.2s ease;
    white-space: nowrap;
}

.action-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.close-button {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.8;
    transition: opacity 0.2s ease;
    margin-left: 12px;
}

.close-button:hover {
    opacity: 1;
}

/* Tooltip */
.tooltip-popup {
    position: absolute;
    top: calc(100% + 22px);
    background: white;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    min-width: 280px;
    z-index: 100;
    cursor: default;
}

.tooltip-popup::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 8px;
    border-style: solid;
    border-color: transparent transparent white transparent;
}

.dark .tooltip-popup {
    background: #1e1e2e;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

.dark .tooltip-popup::after {
    border-color: transparent transparent #1e1e2e transparent;
}

.tooltip-header {
    font-family: 'Poppins', sans-serif;
    font-weight: 500;
    font-size: 13px;
    color: #1e1e2e;
    margin-bottom: 12px;
}

.dark .tooltip-header {
    color: #e0e0e0;
}

.tooltip-list {
    list-style: inherit;
    padding: 0 17px;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.tooltip-item {
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    padding-left: 16px;
    position: relative;
}

.dark .tooltip-item {
    color: #a0a0b0;
}

/* Animations */
.slide-down-enter-active,
.slide-down-leave-active {
    transition: all 0.3s ease;
}

.slide-down-enter-from {
    opacity: 0;
    transform: translateY(-100%);
}

.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-100%);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
